// Finansal › Tahsilat — kartlı analiz sekmesi (design-principles §4): KPI satırı,
// yönteme göre dağılım, borçlular, son ödemeler. Tutarlar kuruş gelir.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, alpha } from "@mui/material";
import {
  PaymentsOutlined as PaymentsIcon,
  PersonOutline as PersonIcon,
  ReceiptLongOutlined as ReceiptIcon,
} from "@mui/icons-material";
import KpiTile from "../../components/molecules/KpiTile";
import { SegmentedControl } from "../../components/atoms";
import { AnalyticsCard, BodySkeleton, CardEmpty, pct } from "../../components/organisms/charts/analyticsParts";
import { useClinicTheme } from "../../theme";
import { useAlert } from "../../context/AlertProvider";
import { COLORS, TYPO, CARD_GAP, CARD_PAGE_PAD } from "../../styles/uiConstants";
import { formatMoney, PAYMENT_METHOD_LABELS } from "../../utils/money";
import { formatPhoneDisplay } from "../../components/atoms/PhoneInput";
import { useGetCollectionsReportMutation, useListPaymentsMutation } from "../../store/billingApi";
import useTerms from "../../hooks/useTerms";

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const PERIODS = [
  { value: "month", label: "Bu ay" },
  { value: "30", label: "Son 30 gün" },
  { value: "year", label: "Bu yıl" },
];

function periodRange(key) {
  const now = new Date();
  if (key === "month") return { from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), to: ymd(now) };
  if (key === "year") return { from: ymd(new Date(now.getFullYear(), 0, 1)), to: ymd(now) };
  const s = new Date(now);
  s.setDate(s.getDate() - 29);
  return { from: ymd(s), to: ymd(now) };
}

const listSx = {
  height: "100%",
  overflowY: "auto",
  overscrollBehavior: "contain",
  mx: -2,
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": { bgcolor: COLORS.slate200, borderRadius: 3 },
};

function ListRow({ onClick, title, sub, value, valueSub, palette }) {
  return (
    <Box
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => onClick && e.key === "Enter" && onClick()}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1.25,
        borderBottom: `1px solid ${COLORS.slate100}`,
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick ? { bgcolor: alpha(palette.primary, 0.03) } : undefined,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: TYPO.body, fontWeight: 500, color: COLORS.slate800 }}>
          {title}
        </Typography>
        {sub ? (
          <Typography noWrap sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }}>
            {sub}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
        <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800, fontVariantNumeric: "tabular-nums" }}>
          {value}
        </Typography>
        {valueSub ? (
          <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }}>{valueSub}</Typography>
        ) : null}
      </Box>
    </Box>
  );
}

export default function CollectionsTab({ token }) {
  const terms = useTerms();
  const navigate = useNavigate();
  const { palette } = useClinicTheme();
  const { showError } = useAlert();
  const [period, setPeriod] = useState("month");
  const range = useMemo(() => periodRange(period), [period]);
  const [loadReport, { data: reportRes, isLoading: loadingReport }] = useGetCollectionsReportMutation();
  const [loadPayments, { data: paymentsRes, isLoading: loadingPayments }] = useListPaymentsMutation();

  useEffect(() => {
    if (!token) return;
    loadReport({ token, ...range })
      .unwrap()
      .catch((e) => showError(e?.data?.message || "Tahsilat raporu alınamadı"));
    loadPayments({ token, ...range, limit: 50 }).unwrap().catch(() => {});
  }, [token, range, loadReport, loadPayments, showError]);

  const report = reportRes?.data?.report;
  const payments = paymentsRes?.data?.list || [];
  const loading = loadingReport || !report;
  const periodLabel = PERIODS.find((p) => p.value === period)?.label;
  const goPatient = (id) => id && navigate(`/Hastalar/${id}?tab=hesap`);

  const methods = report
    ? Object.entries(report.collected.byMethod).map(([key, v]) => ({ key, ...v }))
    : [];
  const maxMethod = Math.max(0, ...methods.map((m) => m.amount));

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        bgcolor: COLORS.slate50,
        ...CARD_PAGE_PAD,
        display: "flex",
        flexDirection: "column",
        gap: CARD_GAP,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate500 }}>
          Kliniğin {terms.patientsLower}dan aldığı ödemeler. Fatura yerine geçmez.
        </Typography>
        <SegmentedControl value={period} onChange={setPeriod} options={PERIODS} ariaLabel="Dönem" />
      </Box>

      <Box sx={{ display: "grid", gap: CARD_GAP, gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(4, 1fr)" } }}>
        <KpiTile
          loading={loading}
          label="Tahsil edilen"
          value={report ? formatMoney(report.collected.total) : ""}
          hint={report ? `${periodLabel} · ${report.collected.count} ödeme` : ""}
        />
        <KpiTile
          loading={loading}
          label="Hizmet ve paket bedeli"
          value={report ? formatMoney(report.charged) : ""}
          hint={
            report
              ? `${report.packageSales.count} paket · ${report.serviceCharges.count} ücretli randevu`
              : ""
          }
        />
        <KpiTile
          loading={loading}
          label="Toplam alacak"
          value={report ? formatMoney(report.totalReceivable) : ""}
          hint="Tüm zamanlar, ödenmemiş bakiye"
          valueColor={report?.totalReceivable > 0 ? COLORS.warningDark : undefined}
        />
        <KpiTile
          loading={loading}
          label={`Borçlu ${terms.patientLower}`}
          value={report ? String(report.debtors.length) : ""}
          hint="Bakiyesi sıfırın üstünde"
        />
      </Box>

      <Box sx={{ display: "grid", gap: CARD_GAP, gridTemplateColumns: { xs: "1fr", lg: "1fr 1.4fr" } }}>
        <AnalyticsCard
          title="Yönteme göre"
          subtitle={periodLabel}
          icon={<PaymentsIcon sx={{ fontSize: 18 }} />}
          bodyHeight={320}
        >
          {loading ? (
            <BodySkeleton kind="list" />
          ) : !report.collected.count ? (
            <CardEmpty
              icon={<PaymentsIcon />}
              title="Bu dönemde ödeme yok"
              description={`${terms.patient} detayındaki Hesap sekmesinden alınan ödemeler burada toplanır.`}
            />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
              {methods.map((m) => (
                <Box key={m.key}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 0.5 }}>
                    <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700 }}>
                      {PAYMENT_METHOD_LABELS[m.key]}
                      <Box component="span" sx={{ color: COLORS.slate400, ml: 1 }}>
                        {m.count} ödeme · %{pct(m.amount, report.collected.total)}
                      </Box>
                    </Typography>
                    <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800, fontVariantNumeric: "tabular-nums" }}>
                      {formatMoney(m.amount)}
                    </Typography>
                  </Box>
                  <Box sx={{ height: 8, borderRadius: 4, bgcolor: COLORS.slate100, overflow: "hidden" }}>
                    <Box
                      sx={{
                        height: "100%",
                        width: `${maxMethod ? Math.max(m.amount ? 3 : 0, (m.amount / maxMethod) * 100) : 0}%`,
                        borderRadius: 4,
                        bgcolor: palette.primary,
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </AnalyticsCard>

        <AnalyticsCard
          title="Borçlular"
          subtitle="Tüm zamanlar · bakiyeye göre"
          icon={<PersonIcon sx={{ fontSize: 18 }} />}
          bodyHeight={320}
        >
          {loading ? (
            <BodySkeleton kind="list" />
          ) : !report.debtors.length ? (
            <CardEmpty
              icon={<PersonIcon />}
              title={`Borçlu ${terms.patientLower} yok`}
              description="Tamamlanan ücretli randevular ve satılan paketler ödenmedikçe burada görünür."
            />
          ) : (
            <Box sx={listSx}>
              {report.debtors.map((d) => (
                <ListRow
                  key={d.patient}
                  palette={palette}
                  onClick={() => goPatient(d.patient)}
                  title={d.name}
                  sub={[d.tellNo ? formatPhoneDisplay(d.tellNo) : null, `Ödenen ${formatMoney(d.paid)}`]
                    .filter(Boolean)
                    .join(" · ")}
                  value={formatMoney(d.balance)}
                  valueSub={`Toplam ${formatMoney(d.charged)}`}
                />
              ))}
            </Box>
          )}
        </AnalyticsCard>
      </Box>

      <AnalyticsCard
        title="Son ödemeler"
        subtitle={periodLabel}
        icon={<ReceiptIcon sx={{ fontSize: 18 }} />}
        bodyHeight={360}
      >
        {loadingPayments && !paymentsRes ? (
          <BodySkeleton kind="list" />
        ) : !payments.length ? (
          <CardEmpty icon={<ReceiptIcon />} title="Bu dönemde ödeme yok" description="Alınan ödemeler en yeniden başlayarak listelenir." />
        ) : (
          <Box sx={listSx}>
            {payments.map((p) => (
              <ListRow
                key={p._id}
                palette={palette}
                onClick={() => goPatient(p.patient)}
                title={p.patientName || terms.patient}
                sub={[
                  PAYMENT_METHOD_LABELS[p.method],
                  p.note || null,
                  p.recordedByName ? `Alan: ${p.recordedByName}` : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
                value={formatMoney(p.amount)}
                valueSub={new Date(p.paidAt).toLocaleString("tr-TR", {
                  day: "2-digit",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              />
            ))}
          </Box>
        )}
      </AnalyticsCard>
    </Box>
  );
}
